import React, { createContext, useContext } from 'react';

/**
 * AppContext - Global shared state for ADHD Planner
 *
 * Contains only UI/navigation state and derived read-only values
 * that are consumed by 3+ components across the tree.
 * Business logic stays in dedicated hooks (useGameification, useMoodTracking, etc.).
 *
 * Shape:
 * {
 *   selectedDate: Date,
 *   currentView: 'daily' | 'weekly',
 *   setCurrentView: (view: string) => void,
 *   showCrisisMode: boolean,
 *   setShowCrisisMode: (val: boolean) => void,
 *   gamificationStats: object,
 *   level: number,
 *   currentStreak: number,
 *   currentMood: string | null,
 *   currentEnergy: number,
 * }
 */
export const AppContext = createContext(null);

/**
 * useAppContext - Custom hook to consume the AppContext.
 * Throws if called outside an AppProvider.
 */
export function useAppContext() {
  const ctx = useContext(AppContext);
  if (ctx === null) {
    throw new Error('useAppContext must be used inside an AppProvider');
  }
  return ctx;
}

/**
 * AppProvider - Thin context provider.
 * The value is assembled in MainApp and passed in as a prop so that
 * business-logic hooks (useGameification, useMoodTracking, …) stay
 * inside MainApp without being moved.
 */
export function AppProvider({ children, value }) {
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
